import { useEffect, useRef, useState } from 'react';
import { operationalCapabilities, technicalCapabilities } from '@/mocks/homeData';

interface Capability {
  icon: string;
  title: string;
  description: string;
}

function CapabilityCard({
  cap,
  index,
  visible,
  spanTwo = false,
}: {
  cap: Capability;
  index: number;
  visible: boolean;
  spanTwo?: boolean;
}) {
  return (
    <div
      className={`group relative bg-white border border-gray-100 rounded-xl p-5 md:p-6 hover:border-[#00AEEF]/30 transition-all duration-300 hover:-translate-y-1 cursor-pointer ${
        spanTwo ? 'lg:col-span-2' : ''
      } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{
        transitionDelay: `${300 + index * 100}ms`,
        boxShadow: '0 1px 12px rgba(0,0,0,0.04)',
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#1E5FAD] to-[#00AEEF] rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="flex items-start gap-4 md:gap-5">
        <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#1E5FAD] to-[#00AEEF]">
          <i className={`${cap.icon} text-xl text-white`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base md:text-lg font-bold text-[#1E5FAD] mb-2 group-hover:text-[#00AEEF] transition-colors">
            {cap.title}
          </h3>
          <p className="text-sm text-[#666666] leading-relaxed">
            {cap.description}
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-1 text-[#00AEEF] text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <span>了解更多</span>
        <i className="ri-arrow-right-line" />
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  visible,
  delay,
  gradientFrom,
  gradientTo,
}: {
  title: string;
  visible: boolean;
  delay: number;
  gradientFrom: string;
  gradientTo: string;
}) {
  return (
    <div
      className={`flex items-center gap-3 mb-8 transition-all duration-600 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div
        className="w-1 h-6 rounded-full"
        style={{
          background: `linear-gradient(to bottom, ${gradientFrom}, ${gradientTo})`,
        }}
      />
      <h3 className="text-lg md:text-xl font-bold text-[#1E5FAD]">{title}</h3>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
  );
}

export default function CoreCapabilities() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 md:py-28 bg-white">
      <div className="w-full px-4 md:px-8 lg:px-16">
        {/* Header - 简化 */}
        <div className="text-center mb-14 md:mb-18">
          <h2
            className={`text-2xl md:text-4xl font-bold text-[#1E5FAD] mb-4 transition-all duration-600 delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            让营销投入产生清晰价值回报
          </h2>
          <p
            className={`text-sm md:text-base text-[#666666] max-w-xl mx-auto transition-all duration-600 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            运营能力 × 技术服务，构建端到端数字化闭环
          </p>
        </div>

        {/* 运营能力 */}
        <div className="mb-14 md:mb-16">
          <SectionHeader
            title="运营能力"
            visible={visible}
            delay={300}
            gradientFrom="#1E5FAD"
            gradientTo="#00AEEF"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {operationalCapabilities.map((cap, index) => (
              <CapabilityCard
                key={index}
                cap={cap}
                index={index}
                visible={visible}
              />
            ))}
          </div>
        </div>

        {/* 技术服务 */}
        <div>
          <SectionHeader
            title="技术服务"
            visible={visible}
            delay={700}
            gradientFrom="#00AEEF"
            gradientTo="#1E5FAD"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {technicalCapabilities.map((cap, index) => (
              <CapabilityCard
                key={index}
                cap={cap}
                index={index + 4}
                visible={visible}
                spanTwo
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}